import React, { useEffect, useState } from 'react';
import { X, Gift, ShieldCheck, AlertTriangle, Clock } from 'lucide-react';

const STORAGE_KEY = 'sg_welcome_popup_v2_dismissed';
const OFFER_END = 'December 31, 2026';

const WelcomePopup = () => {
  const [open, setOpen] = useState(false);
  const [dontShow, setDontShow] = useState(false);

  useEffect(() => {
    const dismissed = typeof window !== 'undefined' && window.localStorage?.getItem(STORAGE_KEY) === '1';
    if (!dismissed) {
      const t = setTimeout(() => setOpen(true), 500);
      return () => clearTimeout(t);
    }
  }, []);

  const close = () => {
    if (dontShow && typeof window !== 'undefined') {
      window.localStorage?.setItem(STORAGE_KEY, '1');
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      data-testid="welcome-popup-overlay"
      className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-300"
      onClick={close}
    >
      <div
        data-testid="welcome-popup"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[880px] rounded-3xl border border-emerald-400/20 bg-gradient-to-br from-[#062017] via-[#0a2f22] to-[#062017] shadow-[0_25px_60px_rgba(6,32,23,0.7)] overflow-hidden"
      >
        {/* Close */}
        <button
          data-testid="welcome-popup-close"
          onClick={close}
          className="absolute top-4 right-4 z-10 rounded-full p-2 text-emerald-100/70 hover:text-white hover:bg-emerald-900/40 transition"
          aria-label="Close"
        >
          <X size={22} />
        </button>

        {/* Ribbon */}
        <div className="absolute top-6 left-0 z-10">
          <div className="bg-amber-500 text-emerald-950 text-[10px] font-black tracking-[0.2em] px-4 py-1 shadow-md uppercase">
            Limited Offer
          </div>
        </div>

        <div className="grid md:grid-cols-[1fr,320px] gap-0">
          {/* LEFT — content */}
          <div className="p-8 md:p-10 pt-14">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-300/80">
              <Gift size={14} /> Welcome Bonus
            </div>
            <h2 className="mt-2 font-serif text-white text-[26px] md:text-[32px] leading-tight tracking-tight">
              Get <span className="text-amber-400">$5 Free</span> on Successful KYC Verification
            </h2>
            <p className="mt-3 text-[13.5px] text-emerald-100/75 leading-relaxed">
              Create your <span className="text-white font-semibold">SkywayGreen</span> account and complete KYC to receive
              <span className="text-amber-300 font-semibold"> $5 </span>
              instantly in your promotional wallet — usable towards your first Investment Package.
            </p>

            <div className="mt-6 space-y-3">
              <Rule
                icon={<Gift size={14} />}
                title="$5 KYC Bonus Credit"
                desc="Received automatically once your KYC is approved by the SkywayGreen team."
              />
              <Rule
                icon={<ShieldCheck size={14} />}
                title="Package Purchase Only"
                desc="This bonus can be applied towards any investment package purchase. Not withdrawable to external wallet."
              />
              <Rule
                icon={<AlertTriangle size={14} />}
                title="One KYC — One Account"
                desc="Each identity document is limited to a single verified account. Multiple accounts on the same KYC will result in immediate termination."
              />
              <Rule
                icon={<Clock size={14} />}
                title={`Valid till ${OFFER_END}`}
                desc="Bonus is credited only for KYC approvals completed before the offer expiry date."
              />
            </div>

            <div className="mt-7 flex items-center justify-between gap-4 flex-wrap">
              <label className="inline-flex items-center gap-2 text-[12.5px] text-emerald-100/70 cursor-pointer select-none">
                <input
                  data-testid="welcome-popup-dont-show"
                  type="checkbox"
                  checked={dontShow}
                  onChange={(e) => setDontShow(e.target.checked)}
                  className="h-4 w-4 rounded border-emerald-500/40 bg-emerald-950 text-emerald-500 focus:ring-emerald-500"
                />
                Do not show anymore
              </label>
              <button
                data-testid="welcome-popup-cta"
                onClick={close}
                className="rounded-xl bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold text-sm px-5 py-2.5 transition shadow-lg shadow-emerald-500/25"
              >
                Continue
              </button>
            </div>
          </div>

          {/* RIGHT — visual */}
          <div className="relative hidden md:flex items-center justify-center bg-gradient-to-br from-emerald-900/40 to-emerald-950/60 border-l border-emerald-500/10 p-6">
            <div className="absolute inset-0 opacity-40 [background:radial-gradient(circle_at_center,#10b981_0%,transparent_60%)] pointer-events-none" />

            <div className="relative w-full flex flex-col items-center">
              {/* $5 medal */}
              <div className="relative">
                <div className="w-[180px] h-[180px] rounded-full bg-gradient-to-br from-amber-300 to-amber-600 flex items-center justify-center shadow-[0_18px_40px_rgba(251,191,36,0.35)] border-4 border-amber-200/60">
                  <div className="text-center">
                    <div className="text-[10px] font-bold text-emerald-950 tracking-[0.24em]">GET</div>
                    <div className="font-serif text-[64px] leading-none text-emerald-950 font-black">$5</div>
                    <div className="text-[9px] font-bold text-emerald-950 tracking-[0.22em] mt-1">KYC BONUS</div>
                  </div>
                </div>
                {/* Star sparkles */}
                <div className="absolute -top-3 -right-2 text-amber-300 text-2xl">✦</div>
                <div className="absolute -bottom-1 -left-3 text-amber-200 text-lg">✦</div>
              </div>

              <div className="mt-6 text-center">
                <div className="text-white font-serif text-[18px]">SkyWay <span className="text-emerald-400">Green</span></div>
                <div className="text-[9.5px] text-emerald-200/60 tracking-[0.28em] mt-1">ECOSYSTEM INFRASTRUCTURE</div>
              </div>

              <div className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-amber-400/50 bg-amber-500/10 px-3 py-1 text-[10.5px] font-semibold text-amber-200 tracking-wider">
                <Clock size={12} /> ENDS {OFFER_END.toUpperCase()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Rule = ({ icon, title, desc }) => (
  <div className="flex items-start gap-3">
    <div className="mt-0.5 rounded-lg bg-emerald-500/15 border border-emerald-500/25 text-emerald-300 p-1.5 shrink-0">
      {icon}
    </div>
    <div className="min-w-0">
      <div className="text-[13px] font-semibold text-white">{title}</div>
      <div className="text-[11.5px] text-emerald-100/60 leading-snug">{desc}</div>
    </div>
  </div>
);

export default WelcomePopup;
